import { useEffect, useState } from "react";
import { api, formatCOP, formatNumber } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { TrendingUp, TrendingDown, Package, AlertTriangle, Users, DollarSign, ShoppingBag, Warehouse, ArrowRight, RefreshCw } from "lucide-react";
import { LineChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid, Area, AreaChart } from "recharts";
import CompartirCatalogoCard from "@/components/CompartirCatalogoCard";
import { toast } from "sonner";

const MESES_ES = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];

/**
 * Dashboard del Director — vista general del negocio.
 * Enfoque: tendencias, salud del inventario, alertas de reordén.
 */
export default function DashboardPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ordenTop, setOrdenTop] = useState("valor"); // valor | unidades
  const [ultimaActualizacion, setUltimaActualizacion] = useState(null);
  const [reconciliando, setReconciliando] = useState(false);

  const reconciliarStock = async () => {
    setReconciliando(true);
    try {
      const { data: res } = await api.post("/admin/reconciliar-stock-despachos");
      const r = res?.resultado || {};
      const nMovs = r.movimientos_creados || 0;
      const nUnid = r.unidades_ajustadas || 0;
      const nPed = r.pedidos_afectados || 0;
      if (nMovs === 0) {
        toast.success("Inventario ya está sincronizado con todos los pedidos históricos");
      } else {
        toast.success(
          `Reconciliación completa · ${nMovs} despacho${nMovs === 1 ? "" : "s"} pendiente${nMovs === 1 ? "" : "s"} aplicado${nMovs === 1 ? "" : "s"} · ${nUnid.toLocaleString("es-CO")} und. descontadas de ${nPed} pedido${nPed === 1 ? "" : "s"}`
        );
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo reconciliar");
    } finally { setReconciliando(false); }
  };

  const load = async () => {
    setLoading(true);
    try {
      // Feb/2026: cache-bust con timestamp para asegurar que el "inventario a
      // costo" refleje despachos/importaciones/archivados en curso.
      const { data } = await api.get(`/reportes/dashboard-director?orden_top=${ordenTop}&_t=${Date.now()}`);
      setData(data);
      setUltimaActualizacion(new Date());
    } catch (e) { console.error("DashboardPage: no se pudo cargar", e); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [ordenTop]);

  // Auto-refresh cuando el director vuelve a la pestaña — así el "inventario a costo"
  // se ve fresco al regresar tras despachar o importar un contenedor.
  useEffect(() => {
    const onFocus = () => { load(); };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
    /* eslint-disable-next-line */
  }, [ordenTop]);

  if (loading || !data) return (
    <div className="h-96 flex items-center justify-center text-muted-foreground">Cargando panel...</div>
  );

  const k = data.kpis;
  const delta = k.delta_pct;
  const upTrend = delta === null ? null : delta >= 0;

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.16em] text-primary/90">Panel del director</div>
          <h1 className="font-display text-4xl md:text-5xl mt-2 text-foreground" style={{ fontStyle: "italic" }}>Beem hoy.</h1>
          <p className="text-muted-foreground mt-1">Los movimientos, el pulso, y qué hay que reordenar.</p>
        </div>
        <button onClick={load} className="h-10 px-4 rounded-full border border-border text-sm hover:bg-accent transition-all" data-testid="dash-refresh">
          <RefreshCw className="w-3.5 h-3.5 inline mr-1.5" /> Actualizar
          {ultimaActualizacion && (
            <span className="ml-2 text-[10px] text-muted-foreground/70 tabular-nums">
              {ultimaActualizacion.toLocaleTimeString("es-CO", { hour: "2-digit", minute: "2-digit" })}
            </span>
          )}
        </button>
        <button
          onClick={reconciliarStock}
          disabled={reconciliando}
          className="h-10 px-4 rounded-full border border-border text-sm hover:bg-accent transition-all disabled:opacity-50"
          title="Recorre los pedidos históricos y aplica cualquier despacho que no haya descontado inventario. Idempotente — no descuenta dos veces."
          data-testid="dash-reconciliar-stock"
        >
          <Warehouse className="w-3.5 h-3.5 inline mr-1.5" />
          {reconciliando ? "Reconciliando..." : "Reconciliar histórico"}
        </button>
      </div>

      {/* Row 1: KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI title={`Ventas · ${MESES_ES[new Date(`${k.hoy || new Date().toISOString().slice(0, 10)}T12:00:00`).getMonth()]}`} value={formatCOP(k.ventas_mes ?? k.ventas_30d)} icon={DollarSign} trend={k.delta_mes_pct ?? delta} highlight
          sub={`Últimos 30 días: ${formatCOP(k.ventas_30d)} · vs mismos días del mes pasado`}
          onClick={() => navigate(`/productos?tab=historial&desde=${k.mes_desde}&hasta=${k.hoy}`)} testid="kpi-ventas-mes" />
        <KPI title="Pedidos · este mes" value={formatNumber(k.pedidos_mes ?? k.pedidos_30d)} icon={ShoppingBag} sub={`${k.clientes_activos_30d} clientes activos en 30 días`}
          onClick={() => navigate(`/pedidos?tab=pedidos&desde=${k.mes_desde}&hasta=${k.hoy}`)} testid="kpi-pedidos-mes" />
        <KPI title="Inventario a costo" value={formatCOP(k.inventario_valor)} icon={Warehouse} sub={`${formatNumber(k.productos_activos)} SKUs activos · descuenta cada despacho`}
          onClick={() => navigate("/productos?tab=inventario")} testid="kpi-inventario" />
        <KPI title="Alertas inventario"
          value={formatNumber((data.stock_critico?.length || 0) + (data.reorden_sugerido?.length || 0))}
          icon={AlertTriangle}
          sub={`${data.stock_critico.length} críticos + ${data.reorden_sugerido.length} por ordenar`}
          warning
          onClick={() => navigate("/panel-stock")}
          testid="kpi-alertas-inventario"
        />
      </div>

      {/* Compartir catálogo */}
      <CompartirCatalogoCard variant="director" />

      {/* Row 2: chart + top */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 p-6 rounded-3xl bg-card border border-border">
          <div className="mb-3">
            <div className="text-xs uppercase tracking-[0.16em] text-muted-foreground font-semibold">Tendencia</div>
            <h3 className="font-display text-2xl mt-1" style={{ fontStyle: "italic" }}>Evolución de ventas · 30 días</h3>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data.serie_ventas}>
                <defs>
                  <linearGradient id="beemRose" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.35} />
                    <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="fecha" tickFormatter={(d) => d.slice(5)} stroke="hsl(var(--muted-foreground))" style={{ fontSize: "10px" }} />
                <YAxis stroke="hsl(var(--muted-foreground))" style={{ fontSize: "10px" }} tickFormatter={(v) => v >= 1e6 ? `${(v/1e6).toFixed(1)}M` : v >= 1e3 ? `${(v/1e3).toFixed(0)}k` : v} />
                <Tooltip formatter={(v) => formatCOP(v)} labelFormatter={(l) => `Fecha: ${l}`} contentStyle={{ borderRadius: 12, borderColor: "hsl(var(--border))", background: "hsl(var(--card))" }} />
                <Area type="monotone" dataKey="total" stroke="hsl(var(--primary))" fill="url(#beemRose)" strokeWidth={2.5} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="p-6 rounded-3xl bg-card border border-border">
          <div className="mb-3 flex items-start justify-between gap-2">
            <div>
              <div className="text-xs uppercase tracking-[0.16em] text-muted-foreground font-semibold">Ranking</div>
              <h3 className="font-display text-xl mt-1" style={{ fontStyle: "italic" }}>Productos más vendidos</h3>
            </div>
            <div className="flex bg-muted/50 border border-border rounded-full p-0.5" title="Cambia el criterio de ordenamiento">
              <button
                onClick={() => setOrdenTop("valor")}
                data-testid="dash-orden-valor"
                className={`px-2.5 h-7 rounded-full text-[10px] font-bold uppercase tracking-wider transition-colors ${ordenTop === "valor" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
                title="Ordenar por dinero vendido"
              >
                $ Valor
              </button>
              <button
                onClick={() => setOrdenTop("unidades")}
                data-testid="dash-orden-unidades"
                className={`px-2.5 h-7 rounded-full text-[10px] font-bold uppercase tracking-wider transition-colors ${ordenTop === "unidades" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
                title="Ordenar por unidades vendidas"
              >
                # Unidades
              </button>
            </div>
          </div>
          <div className="space-y-2">
            {(data.top_productos || []).slice(0, 6).map((p, i) => (
              <div key={p.producto_id || i} className="flex items-center gap-2 p-2 rounded-xl hover:bg-accent/40" data-testid={`top-prod-${i}`}>
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold ${i === 0 ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}>{i + 1}</div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold truncate">{p.producto_nombre}</div>
                  <div className="text-[10px] text-muted-foreground truncate">
                    {ordenTop === "unidades" ? formatCOP(p.subtotal) : `${formatNumber(p.cantidad)} unid.`}
                  </div>
                </div>
                <div className="text-xs font-semibold tabular-nums">
                  {ordenTop === "unidades" ? `${formatNumber(p.cantidad)} unid.` : formatCOP(p.subtotal)}
                </div>
              </div>
            ))}
            {(data.top_productos || []).length === 0 && <div className="py-4 text-center text-xs text-muted-foreground">Sin ventas este mes.</div>}
          </div>
        </div>
      </div>

      {/* Row 3: Inventario crítico + reordén sugerido */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="p-6 rounded-3xl bg-card border border-border">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-xs uppercase tracking-[0.16em] text-amber-600 font-semibold">Alerta</div>
              <h3 className="font-display text-2xl mt-1" style={{ fontStyle: "italic" }}>Stock crítico</h3>
              <p className="text-xs text-muted-foreground mt-0.5">Productos por debajo del mínimo o a punto de agotarse</p>
            </div>
            <button onClick={() => navigate("/productos?filter=bajo-stock")} className="text-xs text-primary hover:underline flex items-center gap-1">
              Ver todos <ArrowRight className="w-3 h-3" />
            </button>
          </div>
          {data.stock_critico.length === 0 ? (
            <div className="py-6 text-center text-sm text-muted-foreground">🌟 Todo el stock está en niveles saludables.</div>
          ) : (
            <div className="space-y-2">
              {data.stock_critico.slice(0, 8).map((s) => (
                <div key={s.id} className="flex items-center gap-3 p-2.5 rounded-xl bg-amber-50/50 dark:bg-amber-500/5 border border-amber-200/50">
                  <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold truncate">{s.nombre}</div>
                    <div className="text-[10px] text-muted-foreground">{s.codigo || s.sku} · Vel: {s.velocidad_diaria || 0}/día</div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-semibold text-amber-700 tabular-nums">{formatNumber(s.stock_actual)} {s.unidad || "u"}</div>
                    {s.dias_restantes !== null && s.dias_restantes !== undefined && (
                      <div className="text-[10px] text-muted-foreground">~{s.dias_restantes}d restantes</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="p-6 rounded-3xl bg-primary/5 border border-primary/20">
          <div className="mb-4">
            <div className="text-xs uppercase tracking-[0.16em] text-primary font-semibold">Reordén sugerido</div>
            <h3 className="font-display text-2xl mt-1" style={{ fontStyle: "italic" }}>Qué ordenar en el próximo contenedor</h3>
            <p className="text-xs text-muted-foreground mt-0.5">Basado en la velocidad de venta de los últimos 30 días</p>
          </div>
          {data.reorden_sugerido.length === 0 ? (
            <div className="py-6 text-center text-sm text-muted-foreground">Nada urgente por ahora. Todo alcanza para 3+ semanas.</div>
          ) : (
            <div className="space-y-2">
              {data.reorden_sugerido.slice(0, 8).map((s) => (
                <div key={s.id} className="flex items-center gap-3 p-2.5 rounded-xl bg-card border border-border/60">
                  <Package className="w-4 h-4 text-primary shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold truncate">{s.nombre}</div>
                    <div className="text-[10px] text-muted-foreground">{s.codigo || s.sku} · vel {s.velocidad_diaria}/día · quedan {s.dias_restantes}d</div>
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Pedir</div>
                    <div className="text-sm font-semibold text-primary tabular-nums">{formatNumber(s.unidades_recomendadas_2_meses)} {s.unidad || "u"}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Row 4: Top clientes */}
      <div className="p-6 rounded-3xl bg-card border border-border">
        <div className="mb-3">
          <div className="text-xs uppercase tracking-[0.16em] text-muted-foreground font-semibold">Clientes</div>
          <h3 className="font-display text-2xl mt-1" style={{ fontStyle: "italic" }}>Los que más compraron este mes</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {(data.top_clientes || []).map((c, i) => (
            <div key={c.cliente_id} className="p-3 rounded-2xl bg-nude/30 border border-border/60" data-testid={`top-cli-${i}`}>
              <div className="flex items-center gap-2 mb-1">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary/25 to-secondary/25 text-primary flex items-center justify-center font-semibold font-display italic">
                  {(c.cliente_nombre || "?").slice(0, 1).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold truncate">{c.cliente_nombre}</div>
                  <div className="text-[10px] text-muted-foreground">{c.zona || "Sin zona"} · {c.pedidos} pedidos</div>
                  <div className="text-[10px] text-primary font-medium truncate" data-testid={`top-cli-vendedor-${i}`}>
                    Vendedor: {c.vendedor_nombre || "Sin asignar"}{c.vendedores > 1 ? ` (+${c.vendedores - 1})` : ""}
                  </div>
                </div>
              </div>
              <div className="text-lg font-semibold text-foreground tabular-nums">{formatCOP(c.total)}</div>
            </div>
          ))}
          {(data.top_clientes || []).length === 0 && <div className="col-span-3 py-4 text-center text-xs text-muted-foreground">Sin ventas este mes.</div>}
        </div>
      </div>
    </div>
  );
}

function KPI({ title, value, icon: Icon, trend, sub, highlight, warning, onClick, testid }) {
  const trendUp = trend === null || trend === undefined ? null : trend >= 0;
  const bg = highlight ? "bg-primary text-primary-foreground border-primary" : warning ? "bg-amber-50 dark:bg-amber-500/5 border-amber-200 dark:border-amber-500/20" : "bg-card border-border";
  const fg = highlight ? "text-primary-foreground/80" : "text-muted-foreground";
  const clickable = typeof onClick === "function";
  const Wrap = clickable ? "button" : "div";
  return (
    <Wrap
      type={clickable ? "button" : undefined}
      onClick={onClick}
      data-testid={testid}
      className={`text-left p-3 md:p-5 rounded-3xl border overflow-hidden ${bg} ${clickable ? "hover:shadow-md hover:-translate-y-0.5 transition-all cursor-pointer" : ""}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className={`text-[10px] uppercase tracking-[0.12em] font-semibold ${fg} min-w-0 flex-1 leading-tight`}>{title}</div>
        <Icon className={`w-4 h-4 flex-shrink-0 ${highlight ? "opacity-80" : "text-muted-foreground/60"}`} strokeWidth={1.8} />
      </div>
      <div
        className={`text-lg md:text-3xl font-semibold mt-2 md:mt-3 tabular-nums ${highlight ? "" : "text-foreground"}`}
        style={{ overflowWrap: "anywhere", wordBreak: "break-word" }}
        title={typeof value === "string" || typeof value === "number" ? String(value) : undefined}
      >
        {value}
      </div>
      {trend !== null && trend !== undefined && (
        <div className={`flex items-center gap-1 mt-1 text-xs font-medium ${trendUp ? (highlight ? "text-primary-foreground" : "text-emerald-600") : (highlight ? "text-primary-foreground" : "text-rose-600")}`}>
          {trendUp ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
          {trend > 0 ? "+" : ""}{trend}% vs mes anterior
        </div>
      )}
      {sub && <div className={`text-[11px] mt-1 ${fg}`}>{sub}</div>}
      {clickable && <div className={`text-[10px] mt-1.5 font-semibold ${highlight ? "text-primary-foreground/80" : "text-primary"}`}>Ver detalle →</div>}
    </Wrap>
  );
}
